import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getAuthProfile,
  loginRequest,
  logoutRequest,
  refreshSessionRequest,
  type AdminAuthProfile,
  type AdminSession,
} from "@/lib/api";

const STORAGE_KEY = "apr_admin_session";
const REFRESH_SKEW_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 5_000;

const ROLE_MODULE_FALLBACK: Record<string, string[]> = {
  admin: [
    "dashboard",
    "clientes",
    "facturacion",
    "operaciones",
    "control_lecturas",
    "prestamos",
    "documentacion_apr",
    "bodega",
    "reportes",
    "configuracion",
  ],
  operator: ["dashboard", "clientes", "operaciones", "control_lecturas", "bodega"],
  cashier: ["dashboard", "clientes", "facturacion"],
  billing: ["dashboard", "clientes", "facturacion"],
  field_reader: ["modulo_terreno"],
  readonly: ["dashboard", "reportes"],
};

type LoginInput = {
  email: string;
  password: string;
};

type SessionContextValue = {
  session: AdminSession | null;
  profile: AdminAuthProfile | null;
  allowedModules: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  companyName: string | null;
  subscriptionOverdue: boolean;
  subscriptionDueDate: string | null;
  availableGateways: string[];
  login: (input: LoginInput) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<AdminAuthProfile | null>;
  markPasswordChanged: () => void;
  hasModule: (moduleCode: string) => boolean;
};

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

function loadSessionFromHash(): AdminSession | null {
  try {
    const hash = window.location.hash;
    if (!hash.includes("owner-access?")) return null;
    const qs = hash.replace(/^.*owner-access\?/, "");
    const p = new URLSearchParams(qs);
    const accessToken = p.get("at");
    const refreshToken = p.get("rt");
    const tenantId = p.get("tid");
    const expiresIn = Number(p.get("ei") ?? 900);
    if (!accessToken || !refreshToken || !tenantId) return null;
    const session: AdminSession = {
      accessToken, refreshToken, tenantId,
      email: "owner-access",
      expiresIn,
      expiresAt: Date.now() + expiresIn * 1000,
      planCode: p.get("pc") || null,
      planFeatures: p.get("pf") ? p.get("pf")!.split(",").filter(Boolean) : [],
    };
    // Limpiar hash de la URL sin recargar
    history.replaceState(null, "", window.location.pathname + window.location.search);
    return session;
  } catch {
    return null;
  }
}

function loadStoredSession(): AdminSession | null {
  try {
    // Prioridad: tokens inyectados desde el owner panel
    const fromHash = loadSessionFromHash();
    if (fromHash) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fromHash));
      return fromHash;
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminSession;
    if (!parsed?.accessToken || !parsed?.refreshToken || !parsed?.tenantId) return null;
    return {
      ...parsed,
      expiresAt: Number(parsed.expiresAt) || Date.now(),
      planCode: parsed.planCode ?? null,
      planFeatures: parsed.planFeatures ?? [],
    };
  } catch {
    return null;
  }
}

function isSessionExpiring(session: AdminSession): boolean {
  return !session.expiresAt || session.expiresAt - Date.now() <= REFRESH_SKEW_MS;
}

function persistSession(session: AdminSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function removeStoredSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

function normalizeModules(profile: AdminAuthProfile | null, session: AdminSession | null): string[] {
  if (!profile) return [];

  // Módulos base desde permisos del rol o fallback
  let base: string[];
  if (Array.isArray(profile.modules) && profile.modules.length > 0) {
    base = Array.from(new Set(profile.modules.map((v) => v.trim()).filter(Boolean)));
  } else {
    const set = new Set<string>();
    for (const role of profile.roles ?? []) {
      for (const m of ROLE_MODULE_FALLBACK[role] ?? []) set.add(m);
    }
    base = Array.from(set);
  }

  // Plan desde el profile (siempre fresco desde /auth/me) con fallback a la sesión
  const planCode = profile.planCode ?? session?.planCode ?? null;
  const planFeatures = (profile.planFeatures?.length ? profile.planFeatures : null) ?? session?.planFeatures ?? [];
  const hasFullAccess = !planCode || planCode === "full" || planCode === "permanent";
  if (!hasFullAccess && planFeatures.length > 0) {
    base = base.filter((m) => planFeatures.includes(m));
  }

  return base.sort((a, b) => a.localeCompare(b));
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(() => loadStoredSession());
  const [profile, setProfile] = useState<AdminAuthProfile | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(() => Boolean(loadStoredSession()));

  function applySession(nextSession: AdminSession): void {
    setSession(nextSession);
    persistSession(nextSession);
  }

  function clearSession(): void {
    setSession(null);
    setProfile(null);
    removeStoredSession();
  }

  /** Levanta el bloqueo por clave temporal una vez que el usuario la cambio. */
  function markPasswordChanged(): void {
    setSession((actual) => {
      if (!actual) return actual;
      const siguiente = { ...actual, mustChangePassword: false };
      persistSession(siguiente);
      return siguiente;
    });
  }

  async function renewSession(activeSession: AdminSession): Promise<AdminSession> {
    const nextSession = await refreshSessionRequest(activeSession);
    applySession(nextSession);
    return nextSession;
  }

  async function refreshProfile(): Promise<AdminAuthProfile | null> {
    if (!session) {
      setProfile(null);
      return null;
    }

    try {
      let activeSession = session;
      if (isSessionExpiring(activeSession)) {
        activeSession = await renewSession(activeSession);
      }
      const nextProfile = await getAuthProfile(activeSession);
      setProfile(nextProfile);
      return nextProfile;
    } catch {
      clearSession();
      return null;
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadProfile(activeSession: AdminSession): Promise<void> {
      setIsBootstrapping(true);
      try {
        const sessionForProfile = isSessionExpiring(activeSession) ? await renewSession(activeSession) : activeSession;
        const nextProfile = await getAuthProfile(sessionForProfile);
        if (!cancelled) setProfile(nextProfile);
      } catch {
        if (!cancelled) {
          clearSession();
        }
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    }

    if (!session) {
      setProfile(null);
      setIsBootstrapping(false);
      return () => {
        cancelled = true;
      };
    }

    void loadProfile(session);
    return () => {
      cancelled = true;
    };
  }, [session?.accessToken, session?.tenantId]);

  useEffect(() => {
    if (!session) return;

    const delay = Math.max(session.expiresAt - Date.now() - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS);
    const timeoutId = window.setTimeout(() => {
      void renewSession(session).catch(() => {
        clearSession();
      });
    }, delay);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [session?.accessToken, session?.expiresAt, session?.refreshToken, session?.tenantId]);

  async function login(input: LoginInput): Promise<void> {
    setIsLoggingIn(true);
    try {
      const nextSession = await loginRequest(input);
      applySession(nextSession);
    } finally {
      setIsLoggingIn(false);
    }
  }

  function logout(): void {
    const activeSession = session;
    clearSession();
    if (activeSession) {
      void logoutRequest(activeSession).catch(() => undefined);
    }
  }

  const allowedModules = useMemo(() => normalizeModules(profile, session), [profile, session]);
  const isLoading = isLoggingIn || isBootstrapping;
  const isAuthenticated = Boolean(session?.accessToken);
  const companyName = profile?.companyName ?? null;

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      profile,
      allowedModules,
      isAuthenticated,
      isLoading,
      companyName,
      subscriptionOverdue: profile?.subscriptionOverdue ?? false,
      subscriptionDueDate: profile?.subscriptionDueDate ?? null,
      availableGateways: profile?.availableGateways ?? [],
      login,
      logout,
      refreshProfile,
      markPasswordChanged,
      hasModule: (moduleCode: string) => {
        if (!isAuthenticated) return false;
        const normalized = moduleCode.trim().toLowerCase();
        if (!normalized) return false;
        return allowedModules.includes(normalized);
      },
    }),
    [allowedModules, isAuthenticated, isLoading, profile, refreshProfile, session],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession debe usarse dentro de SessionProvider");
  }
  return ctx;
}
